import React, { FC, useEffect } from 'react';
import { Navigate, Outlet, Route, Routes } from 'react-router-dom';
import { useIsAuthenticated } from '@azure/msal-react';

import {
  Header,
  Main,
  MainContainer,
  Page,
  Footer,
  Loader,
  NoAccessAlert,
  Slider,
} from '@/ui/components';
import { Home } from '@/modules/Home';
import { OperationsDashboard } from '@/modules/OperationsDashboard';
import { Pipeline } from '@/modules/Pipeline';
import { Storage } from '@/modules/Storage';
import { TransportMovement } from '@/modules/TransportMovement';
import { Vessel } from '@/modules/Vessel';
import { Vin } from '@/modules/Vin';
import { resumePendingInspectionsExport } from '@/api/Exports';
import { AppPaths, AppRoutes } from '@/routes';
import {
  AdminCompanies,
  AdminEmailNotificationAccess,
  AdminInspectionStatusAccessRules,
  AdminJobs,
  JobChainTemplates,
  NewJobChainTemplate,
  TemplateEditor,
  AdminLocations,
  AdminShippingPlan,
  AdminTransportUnits,
  Checklist,
} from '@/modules/SystemAdministration';
import { StockReconciliation } from '@/modules/StockReconciliation';
import { Order } from '@/modules/Order';
import { DocumentManagement } from '@/modules/DocumentManagement';
import { UploadFile } from '@/modules/UploadFile';
import { ManageUsers } from '@/modules/ManageUsers';
import { ShippingPlan } from '@/modules/ShippingPlan';
import { CustomerManagement } from '@/modules/CustomerManagement';
import { useIdTokenClaims } from '@/hooks/useIdTokenClaims';
import {
  SignalRConnection,
  useAppCustomers,
  useHttpAuthReady,
  usePageAccess,
} from '@/hooks';
import { Inspections } from '@/modules/Inspections';
import { Passport } from '@/modules/Passport';
import { PTS } from '@/modules/PTS';
import { Inspection } from '@/modules/Inspection';
import { ManageUserProfile } from '@/modules/ManageUserProfile';
// import { MechanicalReport as MechanicalReportInitial } from '@/modules/Inspection/components/tabs/Documents/components/Reports/Initial/MechanicalReport';
// import { VehicleAppraisalReport as VehicleAppraisalReportInitial } from '@/modules/Inspection/components/tabs/Documents/components/Reports/Initial/VehicleAppraisalReport';
// import { MechanicalReport as MechanicalReportApproved } from '@/modules/Inspection/components/tabs/Documents/components/Reports/Appoved/MechanicalReport';
// import { VehicleAppraisalReport as VehicleAppraisalReportApproved } from '@/modules/Inspection/components/tabs/Documents/components/Reports/Appoved/VehicleAppraisalReport';
import { LocalStorageKeys } from '@/utils/constants';
import { AppUpdate } from './components/AppUpdate';
import { Reports } from '../Reports/Reports';
import { JobChainDetailPage, ManageJobs } from '../Jobs';
import '@/styles/index.scss';
import { loadlanes } from '../loadlanes';
import { CommandCentrePage } from '../commandCentre';
import { DamageExceptions } from '../DamageExceptions';
import {
  QuestionnaireTemplates,
  TemplateEditorPage,
} from '@/modules/QuestionnaireTemplates';
import {
  QuestionnaireResponses,
  ResponseDetailPage,
} from '@/modules/QuestionnaireResponses';
import { ReleaseNotes, ReleaseNotesPopup } from '@/modules/ReleaseNotes';
import { ProductImagesPage } from '@/modules/ProductImages/ProductImages';
import { ProductImagePage } from '@/modules/ProductImages/ProductImageDetail';
import { GenerationRecordPage } from '@/modules/ProductImages/GenerationRecord';
import { ImageBatchPage } from '@/modules/ProductImages/ImageBatches';

export const App: FC = () => {
  const isAuthenticated = useIsAuthenticated();
  const { inProgress, hasLoggedIn } = useIdTokenClaims();
  const {
    userPermissions: { loaded },
  } = useAppCustomers();
  const pages = usePageAccess();
  const isHttpAuthReady = useHttpAuthReady();
  const allPages = pages.flatMap((p) => [p, ...(p.subRoutes || [])]);

  useEffect(() => {
    if (!hasLoggedIn || !isHttpAuthReady) return;
    // initialize WebSocket connection after auth token factory is ready
    const signalRInstance = SignalRConnection.getInstance();
    signalRInstance.initializeConnection().catch(() => undefined);
    void resumePendingInspectionsExport();
  }, [hasLoggedIn, isHttpAuthReady]);

  if (inProgress !== 'none') {
    return <Loader />;
  }

  const getRouteElement = (Component: React.ElementType, route?: string) => {
    const page = allPages.find((p) => p.to === route);
    return loaded ? (
      !page || page.enabled ? (
        <Component />
      ) : (
        <NoAccessAlert />
      )
    ) : null;
  };

  const getRootElement = () => {
    if (!isAuthenticated) {
      return <Home />;
    }

    if (!loaded || !localStorage.getItem(LocalStorageKeys.CustomerIds))
      return null;

    const hasAccessPages = pages.filter((page) => page.enabled);
    if (hasAccessPages.length === 0) return <NoAccessAlert />;
    return <Navigate replace to={hasAccessPages[0].to} />;
  };

  return (
    <>
      <AppUpdate />
      {hasLoggedIn && loaded ? <ReleaseNotesPopup /> : null}
      <Header />
      <Page>
        {hasLoggedIn ? loaded ? <Slider /> : <Loader /> : null}
        <MainContainer>
          <Main>
            <Routes>
              <Route path={AppRoutes.root} element={getRootElement()} />

              {hasLoggedIn ? (
                <>
                  <Route
                    path={AppRoutes.operationsDashboard}
                    element={getRouteElement(
                      OperationsDashboard,
                      AppRoutes.operationsDashboard,
                    )}
                  />
                  <Route
                    path={AppRoutes.pipeline}
                    element={getRouteElement(Pipeline, AppRoutes.pipeline)}
                  />
                  <Route
                    path={AppRoutes.storage}
                    element={getRouteElement(Storage, AppRoutes.storage)}
                  />
                  <Route
                    path={AppRoutes.transportSummary}
                    element={getRouteElement(
                      TransportMovement,
                      AppRoutes.transportSummary,
                    )}
                  />
                  <Route
                    path={AppRoutes.vessels}
                    element={getRouteElement(Vessel, AppRoutes.vessels)}
                  />

                  <Route
                    path={AppRoutes.passport}
                    element={getRouteElement(Passport, AppRoutes.passport)}
                  />
                  <Route
                    path={AppRoutes.pts}
                    element={getRouteElement(PTS, AppRoutes.pts)}
                  />
                  <Route
                    path={AppRoutes.vin}
                    element={getRouteElement(Vin, AppRoutes.vin)}
                  />
                  <Route
                    path={AppRoutes.order}
                    element={getRouteElement(Order, AppRoutes.order)}
                  />
                  <Route
                    path={AppRoutes.shippingPlan}
                    element={getRouteElement(
                      ShippingPlan,
                      AppRoutes.shippingPlan,
                    )}
                  />
                  <Route
                    path={AppRoutes.inspections}
                    element={getRouteElement(
                      Inspections,
                      AppRoutes.inspections,
                    )}
                  />
                  <Route
                    path={AppRoutes.questionnaireResponses}
                    element={getRouteElement(
                      QuestionnaireResponses,
                      AppRoutes.questionnaireResponses,
                    )}
                  />
                  <Route
                    path={`${AppRoutes.questionnaireResponses}/:id`}
                    element={getRouteElement(
                      ResponseDetailPage,
                      AppRoutes.questionnaireResponses,
                    )}
                  />
                  <Route
                    path={AppRoutes.questionnaireTemplates}
                    element={getRouteElement(
                      QuestionnaireTemplates,
                      AppRoutes.questionnaireTemplates,
                    )}
                  />
                  {/* new/:id share the list's access key so page permissions carry over */}
                  <Route
                    path={`${AppRoutes.questionnaireTemplates}/new`}
                    element={getRouteElement(
                      TemplateEditorPage,
                      AppRoutes.questionnaireTemplates,
                    )}
                  />
                  <Route
                    path={`${AppRoutes.questionnaireTemplates}/:id`}
                    element={getRouteElement(
                      TemplateEditorPage,
                      AppRoutes.questionnaireTemplates,
                    )}
                  />
                  <Route
                    path={AppRoutes.damages}
                    element={getRouteElement(
                      DamageExceptions,
                      AppRoutes.damages,
                    )}
                  />
                  <Route
                    path={AppRoutes.reports}
                    element={getRouteElement(Reports, AppRoutes.reports)}
                  />
                  <Route
                    path={AppRoutes.manageUser}
                    element={getRouteElement(ManageUserProfile)}
                  />
                  <Route
                    path={AppRoutes.manageUsers}
                    element={getRouteElement(
                      ManageUsers,
                      AppRoutes.manageUsers,
                    )}
                  />
                  <Route
                    path={AppRoutes.customerManagement}
                    element={getRouteElement(
                      CustomerManagement,
                      AppRoutes.customerManagement,
                    )}
                  />
                  <Route
                    path={AppRoutes.productImages}
                    element={getRouteElement(
                      ProductImagesPage,
                      AppRoutes.productImages,
                    )}
                  />
                  <Route
                    path={`${AppRoutes.productImages}/products/:productId`}
                    element={getRouteElement(
                      ProductImagePage,
                      AppRoutes.productImages,
                    )}
                  />
                  <Route
                    path={`${AppRoutes.productImages}/generations/:generationId`}
                    element={getRouteElement(
                      GenerationRecordPage,
                      AppRoutes.productImages,
                    )}
                  />
                  <Route
                    path={`${AppRoutes.productImages}/batches/:batchId`}
                    element={getRouteElement(
                      ImageBatchPage,
                      AppRoutes.productImages,
                    )}
                  />
                  <Route
                    path={AppRoutes.documentManagement}
                    element={getRouteElement(
                      DocumentManagement,
                      AppRoutes.documentManagement,
                    )}
                  />
                  <Route
                    path={AppRoutes.systemAdministration}
                    element={getRouteElement(
                      Outlet,
                      AppRoutes.systemAdministration,
                    )}
                  >
                    <Route path={AppPaths.jobs} element={<AdminJobs />} />
                    <Route
                      path={AppPaths.jobChainTemplates}
                      element={<JobChainTemplates />}
                    />
                    <Route
                      path={`${AppPaths.jobChainTemplates}/new`}
                      element={<NewJobChainTemplate />}
                    />
                    <Route
                      path={`${AppPaths.jobChainTemplates}/:id`}
                      element={<TemplateEditor mode="view" />}
                    />
                    <Route
                      path={`${AppPaths.jobChainTemplates}/:id/edit`}
                      element={<TemplateEditor mode="edit" />}
                    />
                    <Route
                      path={AppPaths.locations}
                      element={<AdminLocations />}
                    />
                    <Route
                      path={AppPaths.transportation}
                      element={<AdminTransportUnits />}
                    />
                    <Route
                      path={AppPaths.companies}
                      element={<AdminCompanies />}
                    />
                    <Route
                      path={AppPaths.shippingPlan}
                      element={<AdminShippingPlan />}
                    />
                    <Route path={AppPaths.checklist} element={<Checklist />} />
                    <Route
                      path={AppPaths.inspectionStatusAccessRules}
                      element={<AdminInspectionStatusAccessRules />}
                    />
                    <Route
                      path={AppPaths.emailNotificationAccess}
                      element={<AdminEmailNotificationAccess />}
                    />
                    <Route
                      path={`${AppPaths.stockReconciliation}/:id?`}
                      element={<StockReconciliation />}
                    />
                  </Route>

                  <Route
                    path={AppRoutes.inspection}
                    element={getRouteElement(Inspection, AppRoutes.inspection)}
                  />
                  <Route
                    path={AppRoutes.uploadFile}
                    element={getRouteElement(UploadFile, AppRoutes.uploadFile)}
                  />
                  <Route
                    path={AppRoutes.jobs}
                    element={getRouteElement(ManageJobs, AppRoutes.jobs)}
                  />
                  <Route
                    path={`${AppRoutes.jobs}/:jobChainId`}
                    element={getRouteElement(
                      JobChainDetailPage,
                      AppRoutes.jobs,
                    )}
                  />
                  <Route
                    path={AppRoutes.loadlanes}
                    element={getRouteElement(loadlanes, AppRoutes.loadlanes)}
                  />
                  <Route
                    path={AppRoutes.commandCentre}
                    element={getRouteElement(
                      CommandCentrePage,
                      AppRoutes.commandCentre,
                    )}
                  />
                  <Route
                    path={AppRoutes.releaseNotes}
                    element={getRouteElement(
                      ReleaseNotes,
                      AppRoutes.releaseNotes,
                    )}
                  />
                </>
              ) : (
                <Route
                  path="*"
                  element={<Navigate replace to={AppRoutes.root} />}
                />
              )}
            </Routes>
          </Main>
          <Footer />
        </MainContainer>
      </Page>
    </>
  );
};
